// lib/format.ts — Rev9 portal display formatters.
// Every numeric cell must use the right formatter. Tabular numerics aren't
// optional in this product — they are part of the brand.

/**
 * Full-precision USD. Use in tables, statements, and stat blocks where
 * exactness matters. e.g. `$1,247,829.43`
 */
export const fmtUSD = (n: number, decimals = 2): string =>
  "$" + n.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

/**
 * Compact USD with B/M/K suffix. Use ONLY in pure summary contexts —
 * sparkline axis labels, secondary stat sublines, drawdown stats.
 * The voice rule: don't round numbers where exactness matters.
 */
export const fmtUSDcompact = (n: number): string => {
  const abs = Math.abs(n);
  const sign = n < 0 ? "−" : ""; // U+2212 minus, not hyphen
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

/**
 * Full-precision EUR. Use in tables, statements, and stat blocks where
 * exactness matters. e.g. `€1,247,829.43`. Locale forced to `en-EU`-style
 * with `€` prefix so the editorial tabular-numerics still align column-
 * for-column with `fmtUSD`.
 */
export const fmtEUR = (n: number, decimals = 2): string =>
  "€" + n.toLocaleString("en-IE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

/**
 * Currency dispatcher: picks the right formatter for a fund's base
 * currency (or any code passed in). USD funds keep the `$` glyph the
 * SMA pages have always used; EUR funds switch to `€`. Unknown codes
 * fall back to USD-formatting + a small unit annotation so the
 * operator notices the gap instead of seeing a silent `$`.
 */
export const fmtBase = (
  n: number, base_currency: string | undefined, decimals = 2,
): string => {
  const code = (base_currency || "USD").toUpperCase();
  if (code === "USD") return fmtUSD(n, decimals);
  if (code === "EUR") return fmtEUR(n, decimals);
  return `${n.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })} ${code}`;
};

/**
 * Unit PRICE — NAV per unit, HWM per unit, average cost per unit, seed NAV.
 * A price is not a money total: `fmtBase`'s 2-decimal default is right for
 * `$249,356.17` and wrong for `0.99912329`, which it renders as `$1.00`.
 *
 * Operator review, 2026-08-11: the Fix Valuation dialog looked like it was
 * ignoring the management fee. The accrual WAS correct (643.8342 on a 250,000
 * fund) and the strike stored the right 8-decimal price — the DISPLAY rounded
 * the deduction away. n8 prices units at 1.00, so at 2 decimals every price
 * reads `$1.00` until accrued fees pass ~0.5% of the fund, and the price
 * column looks permanently frozen while fees accrue daily. Also the standing
 * editorial rule: don't round where exactness matters.
 *
 * Client-facing surfaces: 4 decimals — clean to read, still visibly moves at
 * a unit price of 1. Ops surfaces: `fmtUnitPriceOps`.
 * (Operator decision 2026-08-11.)
 */
export const fmtUnitPrice = (
  n: number, base_currency: string | undefined,
): string => fmtBase(n, base_currency, 4);

/**
 * Unit price for manager/ops surfaces — 6 decimals (operator decision
 * 2026-08-11): enough to see a single day's fee move a 1.00 unit price
 * (~0.000055/day at 2%/yr), without the full 8 stored decimals in every
 * table cell. The column stays exact to the sixth place; the underlying
 * `FundValuation.nav_per_unit_base` keeps all 8. See `fmtUnitPrice`.
 */
export const fmtUnitPriceOps = (
  n: number, base_currency: string | undefined,
): string => fmtBase(n, base_currency, 6);

/**
 * Asset quantity. Default 4 decimals for crypto; pass 2 for stablecoins / USD.
 */
export const fmtQty = (n: number, decimals = 4): string =>
  n.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

/**
 * Percent return. `+2.40%` for positive, `-1.20%` for negative.
 * Set `withSign=false` for contexts where the column header carries the sign meaning.
 */
export const fmtPct = (n: number, decimals = 2, withSign = true): string => {
  const sign = n > 0 && withSign ? "+" : "";
  return `${sign}${n.toFixed(decimals)}%`;
};

/**
 * Render a USD dollar with a de-emphasized `$` for hero stats.
 * Returns the parts so the caller can wrap the `$` in a `<span className="currency">`.
 * Convention in the prototype: `font-size: 0.58em; vertical-align: 0.18em; color: var(--ink-3)`.
 */
export const splitUSD = (n: number, decimals = 2): { currency: "$"; number: string } => ({
  currency: "$",
  number: n.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }),
});

/**
 * Stablecoins display with 2 decimal places; everything else 4.
 */
export const isStable = (asset: string): boolean =>
  ["USD", "USDC", "USDT", "DAI"].includes(asset.toUpperCase());
